import React, { useEffect, useState } from 'react';
import { ArrowLeft, Star, Award, CheckCircle, AlertCircle, ArrowUpRight, MapPin, Users, Coffee, Wifi, ParkingSquare, Utensils } from 'lucide-react';
import { useVenueStore } from '../store/useVenueStore';
import type { Venue } from '../types/venue';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import { Link } from 'react-router-dom';

// Define the order and images for the top venues
const topVenueImages: Record<string, string> = {
  'svenska-massan': '/assets/images/svenska-massan.webp',
  'hotel-pigalle': '/assets/images/hotel-pigalle-room.jpg',
  'villa-johanneberg': 'https://www.essgroup.se/wp-content/uploads/2018/09/villa-johanneberg-large-top-slide-6-1024x630.jpg',
  'elite-park-avenue': 'https://www.elite.se/globalassets/images/hotell/goteborg/elite-park-avenue-hotel/gotpah_hotell_1.jpg?mode=crop&scale=both&width=1440&height=960',
  'hjortviken-hotel': '/assets/images/hjortviken.jpg',
  'arken-hotel': 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2a/8e/e4/17/caption.jpg?w=900&h=500&s=1',
  'hotel-bellora': 'https://lh3.googleusercontent.com/proxy/P6wQf2AG64XQMXcZYtCHdHTs9tSYtR3P-UXmOLXFVRNg6WqNh6R7PL0zx0m1yKDoAnvLZ8Dm5I9RHr4GH6_0X7ke8PZxBEOf5H_zEidGZ_TNsIu1_ALsQE1cXmKuxiMkLvC3VpQIHZGWlvP86vUTBGLNUvj8vqg=s680-w680-h510',
  'kviberg-hotel': '/assets/images/kviberg-hotel.jpg',
  'ullevi-konferens': '/assets/images/ullevi.jpg',
  'jacyz-hotel': 'https://www.byggnyheter.se/sites/default/files/media-cover-images/jacy_z_pressrelease_arets_byggnad_00.jpg'
};

// Define the specific order of top venues (IDs) - Updated order with Jacy'z at #1, Hjortviken at #2, and Svenska Mässan at #3
const topVenueOrder = [
  'jacyz-hotel',       // #1 
  'hjortviken-hotel',  // #2 (previously #3)
  'svenska-massan',    // #3 (previously #2)
  'hotel-pigalle',     // #4 
  'villa-johanneberg', // #5 
  'elite-park-avenue', // #6 
  'arken-hotel',       // #7 
  'hotel-bellora',     // #8 
  'kviberg-hotel',     // #9 
  'ullevi-konferens'   // #10
];

export const Topplistan: React.FC = () => {
  const { venues } = useVenueStore();
  const [topVenues, setTopVenues] = useState<Venue[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (venues && venues.length > 0) {
      // Create a map of venue ID to venue
      const venueMap = venues.reduce((map, venue) => {
        map[venue.id] = venue;
        return map;
      }, {} as Record<string, Venue>);
      
      // Get ordered top venues based on predefined list
      const orderedVenues = topVenueOrder
        .map(id => venueMap[id])
        .filter(venue => venue !== undefined) as Venue[];
        
      setTopVenues(orderedVenues);
    }
  }, [venues]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Add required props to Header */}
      <Header 
        onMenuClick={() => console.log('Menu clicked')}
        onAboutClick={() => console.log('About clicked')}
        onContactClick={() => console.log('Contact clicked')}
      />
      
      {/* Main content */}
      <div className="flex-1 pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('topplistan.title')}</h1>
              <p className="mt-2 text-lg text-gray-600">{t('topplistan.subtitle')}</p>
            </div>
            <Link 
              to="/"
              className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('topplistan.backToMain')}
            </Link>
          </div>

          {/* Selection criteria */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2 text-indigo-500" />
              {t('topplistan.criteria')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-700 mb-2">{t('topplistan.methodologyTitle')}</h3>
                <p className="text-gray-600 text-sm">{t('topplistan.methodologyText')}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-2">{t('topplistan.rankingTitle')}</h3>
                <p className="text-gray-600 text-sm">{t('topplistan.rankingText')}</p>
              </div>
            </div>
          </div>

          {/* Top venues list */}
          <div className="space-y-8">
            {topVenues.map((venue, index) => (
              <div key={venue.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                  <div className="sm:flex items-start">
                    <div className="flex-shrink-0 mb-4 sm:mb-0 mr-6">
                      <div className="relative">
                        <span className="absolute -top-2 -left-2 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-800 font-semibold text-sm">
                          #{index + 1}
                        </span>
                        <img 
                          src={topVenueImages[venue.id] || venue.image} 
                          alt={venue.name} 
                          className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-lg"
                        />
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3">
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">{venue.name}</h2>
                          <div className="flex items-center space-x-1 mb-2">
                            {[...Array(5)].map((_, index) => (
                              <Star 
                                key={index}
                                size={16}
                                className={`${index < Math.floor(venue.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                            <span className="text-sm ml-1">{venue.rating.toFixed(1)}</span>
                          </div>
                        </div>
                        
                        <Link
                          to={`/venues/${venue.id}`}
                          className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          {t('topplistan.viewVenue')}
                        </Link>
                      </div>
                      
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-6">
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="mr-2 h-4 w-4 text-gray-400" />
                          <span>{t('topplistan.capacity')}: {venue.capacity?.min || 0}-{venue.capacity?.max || 0} {t('people')}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                          <span>{t('topplistan.address')}: {venue.address || ''}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Coffee className="mr-2 h-4 w-4 text-gray-400" />
                          <span>{t('topplistan.amenities')}: {venue.amenities?.join(', ') || ''}</span>
                        </div>
                      </div>
                      
                      {venue.scores && (
                        <div className="mt-4">
                          <h3 className="text-sm font-medium text-gray-700">{t('topplistan.topFeatures')}</h3>
                          <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                            {venue.scores.location !== undefined && (
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-5 w-5 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-blue-600 text-xs font-semibold">{venue.scores.location}</span>
                                </div>
                                <span className="ml-2 text-sm text-gray-600">{t('topplistan.location')}</span>
                              </div>
                            )}
                            
                            {venue.scores.service !== undefined && (
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-5 w-5 bg-green-100 rounded-full flex items-center justify-center">
                                  <span className="text-green-600 text-xs font-semibold">{venue.scores.service}</span>
                                </div>
                                <span className="ml-2 text-sm text-gray-600">{t('topplistan.service')}</span>
                              </div>
                            )}
                            
                            {venue.scores.facilities !== undefined && (
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-5 w-5 bg-purple-100 rounded-full flex items-center justify-center">
                                  <span className="text-purple-600 text-xs font-semibold">{venue.scores.facilities}</span>
                                </div>
                                <span className="ml-2 text-sm text-gray-600">{t('topplistan.facilities')}</span>
                              </div>
                            )}
                            
                            {venue.scores.food !== undefined && (
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-5 w-5 bg-orange-100 rounded-full flex items-center justify-center">
                                  <span className="text-orange-600 text-xs font-semibold">{venue.scores.food}</span>
                                </div>
                                <span className="ml-2 text-sm text-gray-600">{t('topplistan.food')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {venue.pros && venue.pros.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 flex items-center">
                              <CheckCircle className="mr-1 h-4 w-4 text-green-500" />
                              {t('topplistan.pros')}
                            </h3>
                            <ul className="mt-1 pl-4 list-disc text-sm text-gray-600 space-y-1">
                              {venue.pros.map((pro, i) => (
                                <li key={i}>{pro[t('language') as keyof typeof pro] || pro.en || ''}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {venue.cons && venue.cons.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 flex items-center">
                              <AlertCircle className="mr-1 h-4 w-4 text-red-500" />
                              {t('topplistan.cons')}
                            </h3>
                            <ul className="mt-1 pl-4 list-disc text-sm text-gray-600 space-y-1">
                              {venue.cons.map((con, i) => (
                                <li key={i}>{con[t('language') as keyof typeof con] || con.en || ''}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {venue.website && (
                        <div className="mt-4">
                          <a
                            href={venue.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            {t('topplistan.visitWebsite')}
                            <ArrowUpRight className="ml-1 h-3 w-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}; 